import {
  WesterosTagCreateDocument,
  WesterosTagDeleteDocument,
  WesterosTagGetByIdDocument,
  WesterosTagListDocument,
  WesterosTagsPaginatedDocument,
  WesterosTagUpdateDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import {
  IGetTagArgs,
  ITagListReturnType,
  ITagModel,
} from "api/services/library/tags";
import { ResourceEnum, ScopeEnum } from "config";

import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

export async function getWesterosTagsList(
  variables: IGetTagArgs
): Promise<ITagListReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosTagsPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosTagsPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createWesterosTag(
  name: string,
  description: string
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosTagCreateDocument,
      variables: {
        name,
        description,
      },
      fetchPolicy: "no-cache",
    });

    // Evict all tag queries so they refetch with the new item
    executeEviction(client, {
      typename: "WesterosTag",
      fieldNames: ["westerosTagsPaginated", "westerosTagList"],
    });

    return res?.data?.westerosTagCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateWesterosTag(
  tagId: number,
  name: string,
  description: string
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosTagUpdateDocument,
      variables: {
        tagId,
        name,
        description,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "WesterosTag",
      id: tagId.toString(),
      fieldNames: [
        "westerosTagsPaginated",
        "westerosTagList",
        "westerosTagGetById",
      ],
    });

    return res?.data?.westerosTagUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getWesterosTag(tagId: number): Promise<ITagModel> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosTagGetByIdDocument,
      variables: {
        tagId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosTagGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteWesterosTag(tagId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosTagDeleteDocument,
      variables: {
        tagId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "WesterosTag",
      id: tagId.toString(),
      fieldNames: [
        "westerosTagsPaginated",
        "westerosTagList",
        "westerosTagGetById",
      ],
    });

    return Boolean(res?.data?.westerosTagDelete);
  } catch (error) {
    throw error;
  }
}

export async function getWesterosTags(
  scope: ScopeEnum,
  resource: ResourceEnum
): Promise<Pick<ITagModel, "tagId" | "name">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosTagListDocument,
      variables: {
        scope,
        resource,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosTagList ?? [];
  } catch (error) {
    throw error;
  }
}
